import {
  CheckIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  StateCircleIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { REVIEW_STATES, TReviewStates } from '@/constants';
import { ReviewStateEnum, TReviewStateValues } from '@/features/segments/types';

import { StatusSeenChipInTable } from '../StatusSeenChipInTable';

interface ReviewStateFieldProps<T = ReviewStateEnum> {
  value?: ReviewStateEnum;
  onChange: (value: ReviewStateEnum) => void;
  className?: string;
  disabled?: boolean;
  reviewStates?: TReviewStates<T>[];
}

export function ReviewStateField<T = ReviewStateEnum>({
  value,
  onChange,
  className,
  disabled,
  reviewStates = REVIEW_STATES as unknown as TReviewStates<T>[],
}: ReviewStateFieldProps<T>) {
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (optionValue: ReviewStateEnum) => {
    onChange(optionValue);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={clsx('relative', className)} ref={containerRef}>
      <IconButton
        color={IconButtonColorEnum.NONE_DEFAULT}
        onClick={() => setOpen(prev => !prev)}
        size={IconButtonSizeEnum.XS}
        className="flex"
        disabled={disabled}
      >
        <StateCircleIcon size={IconSizeEnum.XS} />
      </IconButton>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-[130px] rounded-md border border-gray-200 bg-white shadow-lg">
          {reviewStates.map(review => {
            const key = review.value as unknown as number;
            return (
              <div
                key={key}
                onClick={() => handleOptionClick(key)}
                className={clsx(
                  'flex w-full cursor-pointer items-center gap-1 px-2 py-1 hover:bg-gray-100'
                )}
              >
                <StatusSeenChipInTable
                  review_state={key as TReviewStateValues}
                  classname="flex-1"
                  reviewStates={reviewStates}
                />
                {value !== undefined && value === key && <CheckIcon size={IconSizeEnum.XS} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
